import React from 'react';
import { Bell, ChevronRight, Clock, Zap, TrendingUp, Sparkles, Globe, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useApp, MOCK_CATEGORIES, MOCK_PRODUCTS } from '../context/AppContext';
import { Screen } from '../types';
import { SearchBar } from '../components/ui/SearchBar';
import { BannerCarousel } from '../components/ui/BannerCarousel';
import { CategoryCard } from '../components/ui/CategoryCard';
import { ProductCard } from '../components/ui/ProductCard';
import { Badge } from '../components/ui/Badge';
export function HomeScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    cart,
    isDarkMode
  } = useApp();
  const flashDeals = MOCK_PRODUCTS.filter(p => p.discount);
  const recommended = MOCK_PRODUCTS.slice(0, 4);
  const electronics = MOCK_PRODUCTS.filter(p => p.categoryId === 'c1');
  const fashion = MOCK_PRODUCTS.filter(p => p.categoryId === 'c2');
  const homeAndLiving = MOCK_PRODUCTS.filter(p => ['c3', 'c12'].includes(p.categoryId));
  const healthAndBeauty = MOCK_PRODUCTS.filter(p => ['c4', 'c11'].includes(p.categoryId));
  const sportsAndGaming = MOCK_PRODUCTS.filter(p => ['c5', 'c10'].includes(p.categoryId));
  const phonesAndAccessories = MOCK_PRODUCTS.filter(p => p.categoryId === 'c6');
  const newArrivals = MOCK_PRODUCTS.slice(-4);
  const topRated = [...MOCK_PRODUCTS].sort((a, b) => b.rating - a.rating).slice(0, 4);
  const budgetFinds = [...MOCK_PRODUCTS].sort((a, b) => a.price - b.price).slice(0, 4);
  const CategorySection = ({
    title,
    icon,
    products,
    categoryId,
    categoryName
  }: {
    title: string;
    icon: React.ReactNode;
    products: typeof MOCK_PRODUCTS;
    categoryId?: string;
    categoryName?: string;
  }) => <section data-id="element-608">
      <div className="flex justify-between items-center mb-3" data-id="element-609">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-610">
          {icon} {title}
        </h2>
        <button onClick={() => navigate(Screen.PRODUCT_LIST, {
        categoryId,
        categoryName: categoryName || title
      })} className="text-sm font-medium text-primary flex items-center" data-id="element-611">
          See All <ChevronRight className="w-4 h-4" data-id="element-612" />
        </button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-613">
        {products.map(product => <div key={product.id} className="w-[140px] shrink-0" data-id="element-614">
            <ProductCard product={product} data-id="element-615" />
          </div>)}
      </div>
    </section>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-616">
      {/* Header */}
      <div className="bg-white sticky top-0 z-40 shadow-sm" data-id="element-617">
        {/* Promo Ticker */}
        <div className="bg-secondary text-white overflow-hidden relative" data-id="element-618">
          <div className="animate-marquee flex whitespace-nowrap py-1.5" data-id="element-619">
            {[...Array(2)].map((_, i) => <div key={i} className="flex items-center gap-6 shrink-0 px-3" data-id="element-620">
                <span className="text-[11px] font-medium flex items-center gap-1.5" data-id="element-621">
                  🔥 <strong className="text-accent-300" data-id="element-622">50% OFF</strong> Summer
                  Electronics Sale
                </span>
                <span className="text-white/30" data-id="element-623">•</span>
                <span className="text-[11px] font-medium flex items-center gap-1.5" data-id="element-624">
                  🎁 Use code{' '}
                  <strong className="text-accent-300 bg-accent/20 px-1.5 py-0.5 rounded text-[10px]" data-id="element-625">
                    JUMMALL20
                  </strong>{' '}
                  for ₦2,000 off
                </span>
                <span className="text-white/30" data-id="element-626">•</span>
                <span className="text-[11px] font-medium flex items-center gap-1.5" data-id="element-627">
                  🚚 <strong className="text-accent-300" data-id="element-628">Free Delivery</strong>{' '}
                  on orders over ₦15,000
                </span>
                <span className="text-white/30" data-id="element-629">•</span>
                <span className="text-[11px] font-medium flex items-center gap-1.5" data-id="element-630">
                  ⚡ Flash Deal:{' '}
                  <strong className="text-accent-300" data-id="element-631">Smart Watch</strong> now
                  ₦149,990
                </span>
                <span className="text-white/30" data-id="element-632">•</span>
                <span className="text-[11px] font-medium flex items-center gap-1.5" data-id="element-633">
                  💳 Pay with card & get{' '}
                  <strong className="text-accent-300" data-id="element-634">extra 5% cashback</strong>
                </span>
                <span className="text-white/30 pr-6" data-id="element-635">•</span>
              </div>)}
          </div>
        </div>

        <div className="px-4 pt-3 pb-3" data-id="element-636">
          <div className="flex justify-between items-center mb-4" data-id="element-637">
            <div className="flex items-center" data-id="element-638">
              <img src={isDarkMode ? 'https://cdn.magicpatterns.com/uploads/qwjBm9dydTfJXkZFWZ9RtX/JUMMALL_UPDATED_LOGO_(1).png' : 'https://cdn.magicpatterns.com/uploads/mcFCQJRzth8wn8inc5aHVy/JUMMALL_UPDATED_LOGO_(2).png'} alt="JUMMALL" className="h-8 w-auto object-contain" data-id="element-639" />
            </div>
            <div className="flex items-center gap-4" data-id="element-640">
              <button onClick={() => navigate(Screen.NOTIFICATIONS)} className="text-gray-600 hover:text-primary transition-colors" data-id="element-641">
                <Badge count={2} data-id="element-642">
                  <Bell className="w-6 h-6" data-id="element-643" />
                </Badge>
              </button>
            </div>
          </div>
          <SearchBar readOnly placeholder="Search for products, brands..." data-id="element-644" />
        </div>
      </div>

      <div className="p-4 space-y-6" data-id="element-645">
        {/* Banners */}
        <BannerCarousel data-id="element-646" />

        {/* Product Sourcing CTA */}
        <div onClick={() => navigate(Screen.PRODUCT_SOURCING)} className="bg-gradient-to-r from-primary-800 to-primary rounded-2xl p-4 text-white shadow-md flex items-center justify-between cursor-pointer active:scale-[0.98] transition-transform overflow-hidden relative" data-id="element-647">
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white/10 to-transparent skew-x-12 translate-x-8" data-id="element-648"></div>
          <div className="flex items-center gap-3 z-10" data-id="element-649">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shrink-0 border border-white/20" data-id="element-650">
              <span className="text-2xl" data-id="element-651">📦</span>
            </div>
            <div data-id="element-652">
              <h3 className="font-bold text-base leading-tight mb-0.5" data-id="element-653">
                Source & Brand
              </h3>
              <p className="text-primary-100 text-xs" data-id="element-654">
                Let us import exclusive products for you
              </p>
            </div>
          </div>
          <div className="w-8 h-8 bg-white text-primary rounded-full flex items-center justify-center shrink-0 z-10 shadow-sm" data-id="element-655">
            <ChevronRight className="w-5 h-5" data-id="element-656" />
          </div>
        </div>

        {/* Categories - 2 rows grid */}
        <section data-id="element-657">
          <div className="flex justify-between items-center mb-3" data-id="element-658">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-659">Categories</h2>
            <button onClick={() => navigate(Screen.CATEGORIES)} className="text-sm font-medium text-primary flex items-center" data-id="element-660">
              See All <ChevronRight className="w-4 h-4" data-id="element-661" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3" data-id="element-662">
            {MOCK_CATEGORIES.slice(0, 8).map(cat => <CategoryCard key={cat.id} category={cat} layout="grid" data-id="element-663" />)}
          </div>
          {/* Second row - scrollable for overflow */}
          <div className="flex gap-4 overflow-x-auto pb-2 mt-3 -mx-4 px-4 scrollbar-hide" data-id="element-664">
            {MOCK_CATEGORIES.slice(8).map(cat => <CategoryCard key={cat.id} category={cat} layout="horizontal" data-id="element-665" />)}
          </div>
        </section>

        {/* Product Sourcing CTA */}
        <button onClick={() => navigate(Screen.PRODUCT_SOURCING)} className="w-full bg-gradient-to-r from-secondary via-primary-800 to-primary-700 rounded-2xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform shadow-md text-left" data-id="element-666">
          <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0" data-id="element-667">
            <Globe className="w-6 h-6 text-accent-300" data-id="element-668" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-669">
            <h3 className="text-white font-bold text-sm mb-0.5" data-id="element-670">
              Source & Brand Your Product
            </h3>
            <p className="text-primary-200 text-xs leading-snug" data-id="element-671">
              We import, brand & deliver exclusive products just for you
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-white/60 shrink-0" data-id="element-672" />
        </button>

        {/* Flash Deals */}
        <section className="bg-accent-50 -mx-4 px-4 py-4 rounded-2xl" data-id="element-673">
          <div className="flex justify-between items-center mb-3" data-id="element-674">
            <div className="flex items-center gap-2" data-id="element-675">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-1.5" data-id="element-676">
                <Zap className="w-5 h-5 text-accent fill-accent" data-id="element-677" /> Flash Deals
              </h2>
              <div className="flex items-center gap-1 bg-error text-white px-2 py-0.5 rounded text-xs font-bold" data-id="element-678">
                <Clock className="w-3 h-3" data-id="element-679" /> 12:45:00
              </div>
            </div>
            <button onClick={() => navigate(Screen.FLASH_DEALS)} className="text-sm font-medium text-primary flex items-center" data-id="element-680">
              See All <ChevronRight className="w-4 h-4" data-id="element-681" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-682">
            {flashDeals.map(product => <div key={product.id} className="w-[140px] shrink-0" data-id="element-683">
                <ProductCard product={product} data-id="element-684" />
              </div>)}
          </div>
        </section>

        {/* Top in Electronics */}
        <CategorySection title="Top in Electronics" icon={<span className="text-base" data-id="element-686">💻</span>} products={electronics} categoryId="c1" categoryName="Electronics" data-id="element-685" />

        {/* Fashion Picks */}
        <CategorySection title="Fashion Picks" icon={<span className="text-base" data-id="element-688">👕</span>} products={fashion} categoryId="c2" categoryName="Fashion" data-id="element-687" />

        {/* Top Rated */}
        <section className="bg-primary-50 -mx-4 px-4 py-4 rounded-2xl" data-id="element-689">
          <div className="flex justify-between items-center mb-3" data-id="element-690">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-1.5" data-id="element-691">
              <TrendingUp className="w-5 h-5 text-primary" data-id="element-692" /> Top Rated
            </h2>
            <button onClick={() => navigate(Screen.RECOMMENDED)} className="text-sm font-medium text-primary flex items-center" data-id="element-693">
              See All <ChevronRight className="w-4 h-4" data-id="element-694" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-695">
            {topRated.map(product => <div key={product.id} className="w-[140px] shrink-0" data-id="element-696">
                <ProductCard product={product} data-id="element-697" />
              </div>)}
          </div>
        </section>

        {/* Home & Living */}
        <CategorySection title="Home & Living" icon={<span className="text-base" data-id="element-699">🏠</span>} products={homeAndLiving} categoryId="c3" categoryName="Home" data-id="element-698" />

        {/* Health & Beauty */}
        <CategorySection title="Health & Beauty" icon={<span className="text-base" data-id="element-701">💄</span>} products={healthAndBeauty} categoryId="c4" categoryName="Beauty" data-id="element-700" />

        {/* Sports & Gaming */}
        <section className="bg-gray-900 -mx-4 px-4 py-4 rounded-2xl" data-id="element-702">
          <div className="flex justify-between items-center mb-3" data-id="element-703">
            <h2 className="text-lg font-bold text-white flex items-center gap-1.5" data-id="element-704">
              <span className="text-base" data-id="element-705">🎮</span> Sports & Gaming
            </h2>
            <button onClick={() => navigate(Screen.PRODUCT_LIST, {
            categoryId: 'c10',
            categoryName: 'Gaming'
          })} className="text-sm font-medium text-accent flex items-center" data-id="element-706">
              See All <ChevronRight className="w-4 h-4" data-id="element-707" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-708">
            {sportsAndGaming.map(product => <div key={product.id} className="w-[140px] shrink-0" data-id="element-709">
                <ProductCard product={product} data-id="element-710" />
              </div>)}
          </div>
        </section>

        {/* Phones & Accessories */}
        <CategorySection title="Phones & Accessories" icon={<span className="text-base" data-id="element-712">📱</span>} products={phonesAndAccessories} categoryId="c6" categoryName="Phones" data-id="element-711" />

        {/* New Arrivals */}
        <CategorySection title="New Arrivals" icon={<Sparkles className="w-5 h-5 text-accent" data-id="element-714" />} products={newArrivals} data-id="element-713" />

        {/* Budget Finds */}
        <section className="bg-accent-50 -mx-4 px-4 py-4 rounded-2xl" data-id="element-715">
          <div className="flex justify-between items-center mb-3" data-id="element-716">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-1.5" data-id="element-717">
              💰 Budget Finds
            </h2>
            <button onClick={() => navigate(Screen.RECOMMENDED)} className="text-sm font-medium text-primary flex items-center" data-id="element-718">
              See All <ChevronRight className="w-4 h-4" data-id="element-719" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-720">
            {budgetFinds.map(product => <div key={product.id} className="w-[140px] shrink-0" data-id="element-721">
                <ProductCard product={product} data-id="element-722" />
              </div>)}
          </div>
        </section>

        {/* Recommended For You */}
        <section data-id="element-723">
          <div className="flex justify-between items-center mb-3" data-id="element-724">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-725">
              Recommended For You
            </h2>
            <button onClick={() => navigate(Screen.RECOMMENDED)} className="text-sm font-medium text-primary flex items-center" data-id="element-726">
              See All <ChevronRight className="w-4 h-4" data-id="element-727" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-728">
            {recommended.map(product => <ProductCard key={product.id} product={product} data-id="element-729" />)}
          </div>
        </section>
      </div>
    </motion.div>;
}